import {
    JobStatus,
    JobStatusEnum,
    JOB_STATUS_ALL_PROCESSING,
} from 'shared/JobStatus';
import {DateTime, DateType} from '@cabinetsbycomputer/datetime';
import type {FieldType, TableButton, TableRowType} from 'shared/types';

export type StatusVariant =
    'processing' | 'pending' | 'production' | 'completed' | 'default';

const PROCESSING_STATUSES = new Set<number>(JOB_STATUS_ALL_PROCESSING);

const PRODUCTION_STATUSES = new Set<number>([
    JobStatusEnum.PRODUCTION,
    JobStatusEnum.GFP_IN_PRODUCTION,
    JobStatusEnum.DELIVERY_DATE_CONFIRMED,
    JobStatusEnum.AWAITING_MANUFACTURER_FREIGHT_COST,
]);

const COMPLETED_STATUSES = new Set<number>([
    JobStatusEnum.COMPLETED,
    JobStatusEnum.GFP_DELIVERED,
]);

export const getStatusVariant = (status: number): StatusVariant => {
    if (PROCESSING_STATUSES.has(status)) return 'processing';
    if (PRODUCTION_STATUSES.has(status)) return 'production';
    if (COMPLETED_STATUSES.has(status)) return 'completed';
    if (status === JobStatusEnum.DELETED) return 'default';
    return 'pending';
};

export const JOB_STATUS_LABELS = new Map<number, string>(
    Object.entries(JobStatus).map(([status, label]) => [Number(status), label])
);

export const DATE_FIELD_PRIORITY = [
    'dateUpdatedDateTime',
    'dateUpdated',
    'dateSubmittedDateTime',
    'dateSubmitted',
    'dateAcceptedDateTime',
    'dateAccepted',
    'dateDeliveryDateTime',
    'dateDelivery',
];

const ACTION_LABELS = new Map<string, string>([
    ['Edit Job', 'Edit'],
    ['Copy Job', 'Copy'],
    ['Get PDF', 'Print'],
    ['Delete Job', 'Delete'],
]);

export const getActionLabel = (title: string) =>
    ACTION_LABELS.get(title) ?? title;

export const formatFieldValue = (field: FieldType, row: TableRowType) => {
    const value = row[field.fieldName];

    return field.format ? field.format(value, row) : value;
};

export const formatEnteredDate = (value: unknown): string => {
    if (value === null || typeof value === 'undefined' || value === '') {
        return '-';
    }

    try {
        const date =
            typeof value === 'object' && value !== null && 'date' in value
                ? DateTime.parse((value as {date: string}).date)
                : DateTime.parse(value as string, DateType.ISO);

        const formatted = date.format('d MMM yyyy, h:mm a').toString();
        const yearMatch = formatted.match(/\b\d{4}\b/);

        if (yearMatch && parseInt(yearMatch[0], 10) <= 1970) {
            return '-';
        }

        return formatted;
    } catch (e) {
        return '-';
    }
};

export const isButtonHidden = (
    button: TableButton,
    row: TableRowType
): boolean => {
    if (typeof button.isHidden !== 'undefined') {
        return button.isHidden(row);
    }

    if (button.hasOwnProperty('hidden')) {
        return !!button.hidden;
    }

    return false;
};
